import { selectReportTimeseriesDataSession } from "@/registration-tools/utils/multi-registration-report";
import {
  isLightweightDataSession,
  isLightweightTimeSeriesDataSession,
} from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightAncestor,
  selectLightweightElement,
} from "@/store/lightweight-elements-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  GenericRegistrationTask,
  isC2CRegistrationTask,
  isRegisterMultiCloudDataSetTask,
} from "@/utils/background-tasks";
import { redirectToMultiRegistrationTool } from "@/utils/redirects";
import { NoTranslate } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import type { JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type RegistrationTaskCardProps = {
  /** The task to show a registration card for */
  task: GenericRegistrationTask;
};

/**
 * @returns A card to track the progress of a registration task.
 */
export function RegistrationTaskCard({
  task,
}: RegistrationTaskCardProps): JSX.Element | null {
  const { projectApiClient } = useApiClientContext();

  const taskElement = useAppSelector(
    selectLightweightElement(task.iElementId ?? undefined),
  );
  const dataSessionSection = useAppSelector((state) =>
    selectLightweightAncestor(taskElement, isLightweightDataSession)(state),
  );
  const dataSessionTimeSeries = useAppSelector((state) =>
    selectLightweightAncestor(
      taskElement,
      isLightweightTimeSeriesDataSession,
    )(state),
  );

  const displayElement = isC2CRegistrationTask(task)
    ? dataSessionSection
    : dataSessionTimeSeries;

  const store = useAppStore();

  // Redirect to the multi cloud view for the registered dataset
  const action: CardAction | undefined =
    isRegisterMultiCloudDataSetTask(task) &&
    task.state === BackgroundTaskState.succeeded
      ? {
          name: "Review results",
          callback: () => {
            const registrationReport = task.result;
            assert(
              registrationReport,
              "Registration report missing or with invalid format",
            );

            const reportDatasession = selectReportTimeseriesDataSession(
              registrationReport,
            )(store.getState());
            assert(
              reportDatasession,
              "No datasession detected for registration report",
            );

            redirectToMultiRegistrationTool({
              projectId: projectApiClient.projectId,
              datasetId: reportDatasession.id,
              registrationTaskId: task.id,
            });
          },
        }
      : undefined;

  // The "4D Sessions" name is hard-coded for these elements in the project tree
  // We follow the same format for consistency
  let cardName: string | JSX.Element = "Registration";
  if (displayElement && isLightweightTimeSeriesDataSession(displayElement)) {
    cardName = "4D Sessions";
  } else if (displayElement?.name) {
    cardName = <NoTranslate>{displayElement.name}</NoTranslate>;
  }

  return (
    <GenericCardLayout
      name={cardName}
      subText={getTaskTypeDescription(task)}
      startTime={task.createdAt}
      action={action}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress label={getTaskStatusLabel(task)} task={task} />
      )}
      {task.state === BackgroundTaskState.failed && <CardError task={task} />}
    </GenericCardLayout>
  );
}
